import { useEffect, useState } from 'react';

/**
 * Drop-in replacement for MUI's useMediaQuery.
 * Accepts a raw media query string or a breakpoint pixel value.
 *
 * Usage:
 *   const isMobile = useMediaQuery('(max-width: 904px)');
 *   const isDesktop = useMediaQuery(`(min-width: ${MD_BREAKPOINT}px)`);
 */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    const mql = window.matchMedia(query);
    const handler = (e: MediaQueryListEvent) => setMatches(e.matches);

    // Set initial value in case SSR/hydration mismatch
    setMatches(mql.matches);

    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, [query]);

  return matches;
}

export default useMediaQuery;
